/**
 * Поверителност / Privacy. The Bulgarian is the authoritative text; the
 * English is a translation of it.
 *
 * Every factual sentence has a `verified:` comment beside it naming the file
 * that makes it true, so a change to the system shows which sentence it
 * breaks. A claim nobody could verify is not here: it went to the maintainer
 * as a question instead. The "last updated" date is this file's last commit
 * (vite.config.ts), so editing it dates the page.
 */

import { Link } from 'react-router'

export function PrivacyBg() {
  return (
    <>
      <h2>Кой отговаря за данните</h2>
      <p>
        {/* verified: the maintainer's instruction, 27 Sep (name as the controller) */}
        Сайтът се поддържа от едно лице, не от фирма: Петър Кръстанов, който отговаря за данните ви. За всичко, свързано с тях, пишете на{' '}
        {/* verified: src/worker/routes/contact.ts (CONTACT_ADDRESS); delivery tested by the maintainer, 27 Sep */}
        <a href="mailto:contact@chetiporeda.com">contact@chetiporeda.com</a> или през{' '}
        <Link to="/contact">формата за контакт</Link>.
      </p>

      <h2>Какво се събира и защо</h2>
      <h3>Ако си направите профил</h3>
      <ul>
        {/* verified: migrations/0002_better_auth.sql (user.email) */}
        <li>Имейл адресът ви — за да влизате в профила и да получавате писмата за потвърждение и за нова парола.</li>
        {/* verified: migrations/0002_better_auth.sql (user.name); src/app/routes/Register.tsx and Account.tsx (optional, editable, empty means none); src/app/components/Layout.tsx (shown to the reader in the header) */}
        <li>
          Псевдоним, ако решите да си изберете: по желание, можете да го смените или изтриете от профила си. Виждате го само вие, освен ако
          не напишете отзив: тогава се показва до него. Пази се, докато не го промените или не изтриете профила.
        </li>
        {/* verified: migrations/0002_better_auth.sql (account.password); node_modules/better-auth/dist/crypto/password.mjs (scrypt, the default: src/worker/auth.ts sets no other) */}
        <li>Паролата ви — пази се само нейният хеш (scrypt), никога самата парола.</li>
        {/* verified: src/worker/auth.ts (databaseHooks.session.create.before blanks ipAddress and userAgent); migrations/0010_session_forgets_ip_and_browser.sql (cleared the rows written before) */}
        <li>Докато сте влезли: запис за сесията с таен ключ и срок. IP адресът и браузърът, от които сте влезли, не се пазят в него.</li>
        {/* verified: src/worker/auth.ts (VERIFICATION_TOKEN_TTL_SECONDS = DAY, RESET_TOKEN_TTL_SECONDS = 1 h); migrations/0005_consumed_tokens.sql (only a hash of a used verification token) */}
        <li>Връзките за потвърждение и за нова парола: първата важи 24 часа, втората — 1 час. Всяка работи само веднъж.</li>
      </ul>
      <h3>Какво отбелязвате в профила си</h3>
      <ul>
        {/* verified: migrations/0011_shelf_owned_and_note.sql (status, owned, rating, note); src/worker/routes/me.ts (only the signed-in reader's own rows) */}
        <li>
          Книгите, които сте отбелязали: дали искате да ги прочетете, четете ги или сте ги прочели, дали ги имате, оценките ви от 1 до 5
          и бележките ви. Напредъкът по поредиците се изчислява от тях и не се пази отделно.
        </li>
        {/* verified: migrations/0012_quiz_results.sql (answers, first_series, second_series, taken_at; one row per world) */}
        <li>Последният ви резултат от теста: отговорите, двете поредици, които показа, и кога.</li>
      </ul>
      <p>
        {/* verified: src/worker/routes/me.ts (a verified session, only the caller's own rows); no public profile exists */}
        Виждате ги само вие. Няма публичен профил и никой друг не може да ги прочете. Пазят се, докато не ги промените или не изтриете
        профила.{' '}
        {/* verified: src/worker/routes/reviews.ts (POST /:world/publish-note/:work); src/worker/db/queries/reviews.ts (publishNote: a new review from the note, or the note appended to the live one; the note cleared, or its entry deleted, in the same batch) */}
        Ако публикувате бележка като отзив, текстът ѝ става публичен — като нов отзив или добавен в края на отзива, който вече имате за
        книгата, — а бележката се изчиства.
      </p>

      {/* Approved by the maintainer, 30 Sep (reviews, issue #89). */}
      <h3>Ако напишете отзив</h3>
      <ul>
        {/* verified: migrations/0013_reviews.sql (body, spoilers, created_at, updated_at); src/worker/db/queries/reviews.ts (visibleReviews joins user.name on every request, an empty name shows as „Читател“; reviews are not in render-body.ts's 60-second memo); src/worker/render-body.ts (a book page's reviews are in the server-rendered HTML, so crawlers see them). The search-engine clause and the nickname clause added at the maintainer's request, 30 Sep. src/worker/routes/reviews.ts and queries/reviews.ts (a removed review can be neither edited, 403, nor deleted: deleteOwnReview skips removed rows). */}
        <li>
          Отзивът е публичен: вижда се на страницата на книгата от всеки, с псевдонима ви до него, а ако нямате псевдоним — като
          „Читател“. Понеже е публичен, могат да го намерят и търсачките. Ако смените или премахнете псевдонима си, всичките ви отзиви
          показват веднага новия или „Читател“; търсачките може да пазят стария, докато не посетят страницата отново. Пазим текста му,
          дали разкрива сюжета и кога е написан и променен. Докато не е премахнат, можете да го изтриете по всяко време и да го
          редактирате, освен ако писането на отзиви ви е спряно (вижте по-долу).
        </li>
        {/* DRAFT (strikes, #89) for the maintainer's approval, 1 Oct; the strike bullet's wording is the maintainer's, 5 Oct (impersonal „не може“, as on the terms page). verified: migrations/0013_reviews.sql (removed_at, removed_reason; cascade from user; a partial unique index, one live review per reader per book); src/shared/reviews.ts (writingState: strikes are the reader's removed reviews; two suspend writing and editing for 30 days from the second removal, three end it); src/worker/routes/reviews.ts (the PUT refuses while suspended or after the third; DELETE is never refused; the removal email states the strike); tests/e2e/reviews.spec.ts, tests/unit/review-strikes.test.ts. */}
        <li>
          Ако поддържащият сайта премахне отзив, отзивът престава да се показва, а причината ви се изпраща по имейл. Премахнатият отзив и
          причината се пазят скрити, докато не изтриете профила. За същата книга можете да напишете нов отзив.
        </li>
        <li>
          Всяко премахване се брои. След второто премахване на отзив не може да пишете и редактирате отзиви 30 дни, а след третото —
          вече не може да пишете отзиви в сайта. Профилът, отбелязаните книги, оценките, бележките и резултатът от теста остават, а
          отзивите ви, които не са премахнати, може да изтриете по всяко време. Премахването на отзив се запазва като действие. Този запис се нулира при изтриване на профила. Ако смятате, че е станала грешка, пишете ни.
        </li>
        {/* verified: migrations/0013_reviews.sql (review_reports: review_id, reason, created_at, nothing about the sender) */}
        <li>
          Всеки може да докладва отзив, дори без профил. Към доклада пазим само причината, ако е написана, и нищо за това кой го е
          изпратил. Докладът се изпраща на поддържащия сайта по имейл.
        </li>
      </ul>

      <h3>Ако ни пишете</h3>
      <p>
        {/* verified: src/worker/routes/contact.ts (name, email optional; message required; sent by mail, not written to the database) */}
        Формата за контакт иска само съобщение. Име и имейл са по желание — имейлът е само за да можем да ви отговорим.
        Съобщението се изпраща по имейл и не се записва в базата данни на сайта.
      </p>

      <h3>Какво идва от само себе си</h3>
      <ul>
        {/* verified: wrangler.jsonc (a Cloudflare Worker); docs/decisions/005-headers-on-documents.md (every page passes through it); https://www.cloudflare.com/privacypolicy/ */}
        <li>
          Сайтът работи в Cloudflare. Като хост на сайта Cloudflare обработва всяка заявка — вижда IP адреса ви, браузъра и страницата,
          която сте поискали — и пази собствени записи за тях по{' '}
          <a href="https://www.cloudflare.com/privacypolicy/" rel="noopener">своята политика за поверителност</a>.
        </li>
        {/* verified: inbox/FINDINGS-privacy-evidence.md (observed on production, 27 Sep); src/worker/middleware/security-headers.ts (script-src allows static.cloudflareinsights.com) */}
        <li>
          Посещенията се броят с Cloudflare Web Analytics. Скриптът се добавя от Cloudflare и се зарежда от
          static.cloudflareinsights.com, а отчетът за посещението се изпраща до самия сайт. Той не слага бисквитка и не пази нищо в
          браузъра ви.
        </li>
        {/* verified: src/worker/auth.ts (rateLimit, storage: database, keyed by cf-connecting-ip); src/worker/throttle.ts (a keyed hash, never the address); src/worker/routes/contact.ts (the contact limit is per IP); src/worker/routes/reviews.ts (reports counted per IP, reviews per account) */}
        {/* Approved by the maintainer, 30 Sep (reviews, #89): „докладване на отзив“ and the sentence on reviews added to approved text. */}
        <li>
          За защита от злоупотреби сайтът брои опитите за регистрация, вход, нова парола, изпращане на съобщение и докладване на отзив
          — по IP адрес, а при опитите за конкретен имейл пази само шифриран отпечатък, не самия адрес. Написаните отзиви се броят по
          профил.
        </li>
        {/* verified: wrangler.jsonc (observability enabled); src/worker/log.ts (maskEmail keeps the first letter and the domain) */}
        <li>Сайтът води технически записи за грешки в Cloudflare. В тях имейл адресите са скрити — остават само първата буква и домейнът.</li>
      </ul>

      <h2>На какво основание</h2>
      <ul>
        {/* verified: the maintainer's instruction, 27 Sep; .claude/skills/legal-pages/SKILL.md (account data: performance of a contract) */}
        <li>Данните на профила — защото са нужни, за да ви дадем профила, който сте поискали (изпълнение на договор).</li>
        {/* verified: the maintainer's instruction, 27 Sep; .claude/skills/legal-pages/SKILL.md (security and rate limiting: legitimate interest) */}
        <li>Броенето на опити, защитата срещу ботове, преброяването на посещенията и формата за контакт — на основание законен интерес: да пазим сайта от злоупотреби, да знаем колко хора го четат и да можем да отговорим, когато ни пишат.</li>
      </ul>

      <h2>Какво не се събира</h2>
      <p>
        {/* verified: src/worker/middleware/security-headers.ts (script-src: self, Turnstile, Cloudflare analytics only; no ad or tracking host can load) */}
        Няма реклами, няма проследяване между сайтове, няма профилиране и няма продажба на данни. Никакво решение за вас не се взема
        автоматично.
      </p>

      <h2>Бисквитки и съхранение в браузъра</h2>
      <ul>
        {/* verified: src/worker/auth.ts (session.expiresIn 30 days, disableSessionRefresh, httpOnly, sameSite lax, useSecureCookies in production; no cookieCache, no rememberMe:false, so no other Better Auth cookie) */}
        {/* Approved by the maintainer, 30 Sep (#38). The second sentence changed with the ceiling: it said the cookie expired 30 days after the last visit and that each visit renewed it. */}
        <li>
          Само една бисквитка, и то само ако влезете: бисквитката на сесията. Тя е недостъпна за скриптове и изтича 30 дни след
          като сте влезли. Излизането от профила я прекратява веднага.
        </li>
        {/* verified: src/worker/auth.ts (session.expiresIn 30 days, disableSessionRefresh: true); tests/e2e/session-cap.spec.ts (a used session is not extended, and is refused past 30 days); issue #38 */}
        <li>След като влезете в профила си, сесията ви важи максимум 30 дни. След това трябва да влезете отново, дори да сте ползвали сайта междувременно.</li>
        {/* verified: src/app/i18n/current-locale.ts (LOCALE_STORAGE_KEY = 'chetiporeda.locale', written only by the language switcher) */}
        <li>Ако смените езика, изборът ви се пази в браузъра (localStorage, ключ chetiporeda.locale). Нищо друго не се пази там.</li>
      </ul>

      <h2>Кой друг вижда данните</h2>
      <ul>
        {/* verified: wrangler.jsonc (Workers, D1 binding); src/worker/turnstile.ts (sends the widget's token and the visitor's IP to siteverify) */}
        <li>
          <strong>Cloudflare</strong> — хостинг, мрежа, базата данни и анализът на посещенията. Обработва данните по своето{' '}
          {/* verified: https://www.cloudflare.com/cloudflare-customer-dpa/ (Cloudflare Data Processing Addendum; EU SCCs for restricted transfers) */}
          <a href="https://www.cloudflare.com/cloudflare-customer-dpa/" rel="noopener">Допълнение за обработка на данни</a>, което за
          пренос извън ЕИП прилага стандартните договорни клаузи на ЕС.{' '}
          {/* verified: https://www.cloudflare.com/gdpr/subprocessors/ (Cloudflare's own sub-processor list; linked rather than copied, so it cannot go stale). Added 30 Sep, #65. */}
          <a href="https://www.cloudflare.com/gdpr/subprocessors/" rel="noopener">Подизпълнителите му</a> са изброени в списък, който
          поддържа.
        </li>
        <li>
          {/* verified: src/worker/turnstile.ts (the token and remoteip go to siteverify); https://www.cloudflare.com/turnstile-privacy-policy/ */}
          {/* Approved by the maintainer, 30 Sep (reviews, #89): „докладването на отзив“ added; src/worker/routes/reviews.ts (verifyTurnstile on report) */}
          <strong>Cloudflare Turnstile</strong> — проверката срещу ботове при регистрация, нова парола, формата за контакт и
          докладването на отзив. Обработва
          IP адреса ви, TLS отпечатъка и браузъра ви, и ключа на сайта с адреса му. Когато спира ботове, Cloudflare обработва тези
          данни от името на сайта; когато ги използва, за да подобрява самия Turnstile, го прави като самостоятелен администратор, на
          основание своя законен интерес (<a href="https://www.cloudflare.com/turnstile-privacy-policy/" rel="noopener">политиката на
          Turnstile</a>).
        </li>
        {/* verified: src/worker/mail.ts (resend.emails.send: to, subject, text, html) */}
        {/* verified: Resend dashboard, mail.chetiporeda.com sending region eu-west-1, Ireland (the maintainer, 30 Sep); https://resend.com/docs/dashboard/domains/regions (the region sets where mail is sent from, not where data is stored; stored in the US); https://resend.com/security/gdpr (message content and delivery logs stored in the US, retained 30 days while the account is active); https://resend.com/legal/dpa (updated 2025-12-31: §6.2 ex-EEA transfers are made pursuant to the EU SCCs; Exhibit A retains customer data as long as the agreement is active and deletes it within 90 days of termination — the outer bound for all customer data, including account and billing records, of which the 30 days for content and logs is the specific case; the DPF is a separate certification, §6.7 and §11, not the transfer clause, so it is not named); https://resend.com/legal/subprocessors (linked, not copied). #65, approved by the maintainer 30 Sep. */}
        <li>
          <strong>Resend</strong> — изпраща писмата на сайта. Получава адреса, на който пишем, и цялото писмо, включително връзката в
          него. При формата за контакт получава съобщението ви, а също името и имейла, ако сте ги дали. Писмата тръгват от Ирландия, но
          Resend пази копие от тях и записите за изпращането им в САЩ — според Resend, 30 дни. Преносът е по стандартните договорни
          клаузи на ЕС в неговото{' '}
          <a href="https://resend.com/legal/dpa" rel="noopener">Допълнение за обработка на данни</a>.{' '}
          <a href="https://resend.com/legal/subprocessors" rel="noopener">Подизпълнителите му</a> са изброени в списък, който
          поддържа.
        </li>
        {/* verified: src/app/routes/Book.tsx (rel="nofollow noopener"); src/worker/middleware/security-headers.ts (Referrer-Policy strict-origin-when-cross-origin) */}
        {/* verified: the maintainer's instruction, 27 Sep (Email Routing forwards contact@ and hello@ to the maintainer's Google mailbox); src/worker/routes/contact.ts */}
        <li>
          <strong>Google</strong> — съобщенията от формата за контакт и писмата до адресите на сайта се препращат до пощенската кутия
          на поддържащия сайта, която е при Google.
        </li>
        <li>
          <strong>Книжарниците, към които водят връзките</strong> — само ако щракнете връзка към някоя от тях. Тогава браузърът ви ѝ
          казва, че идвате от chetiporeda.com, но не и от коя страница.
        </li>
      </ul>

      <h2>Къде са данните</h2>
      <p>
        {/* verified: `wrangler d1 info chetiporeda-eu`, 30 Sep: jurisdiction eu, read replication disabled; wrangler.jsonc binds DB to it (runbook docs/runbooks/d1-eu-jurisdiction.md); https://developers.cloudflare.com/d1/configuration/data-location/ (a jurisdiction guarantees where the database runs and stores data, not where the Worker reading it runs); the Worker runs in the data centre nearest the reader (Cloudflare Workers). Wording approved by the maintainer, 30 Sep. */}
        Базата данни е в Cloudflare, под юрисдикцията на ЕС: съхранява се и работи в Европейския съюз и не се копира извън него.
        Страниците, които я четат, се обслужват от най-близкия до вас център на Cloudflare, който може да е и извън ЕС.
      </p>

      <h2>Колко дълго се пазят</h2>
      <ul>
        {/* verified: src/worker/auth.ts (user.deleteUser enabled); migrations/0003_profiles_and_shelves.sql, 0011 and 0012 (ON DELETE CASCADE); tests/e2e/account-deletion.spec.ts, tests/e2e/shelves.spec.ts */}
        {/* DRAFT (#25) for the maintainer's approval: the last sentence. verified: src/worker/auth.ts (user.deleteUser.afterDelete sends accountDeletedEmail after the deletion); tests/e2e/account-deletion.spec.ts */}
        <li>
          Данните на профила, заедно с отбелязаните книги и резултата от теста — докато не го изтриете. Изтриването става веднага и
          премахва всичко, свързано с профила. В момента на изтриването сайтът изпраща едно последно писмо на адреса на профила, че профилът е
          изтрит.
        </li>
        {/* verified: src/worker/scheduled.ts (UNVERIFIED_ACCOUNT_TTL_DAYS = 7, nightly); tests/e2e/scheduled-purge.spec.ts */}
        <li>Непотвърден профил се изтрива след 7 дни.</li>
        {/* verified: src/worker/scheduled.ts (expired verification rows, throttle buckets and consumed token hashes purged nightly) */}
        <li>Връзките за потвърждение и нова парола и броячите на опити по имейл се изтриват всяка нощ, след като изтекат.</li>
        {/* verified: src/worker/scheduled.ts (purgeRateLimitCounters, RATE_LIMIT_RETENTION_HOURS = 1, nightly); tests/e2e/mail-and-retention.spec.ts */}
        <li>Броячите на опити по IP адрес се изтриват при нощното почистване, щом станат по-стари от час — тоест живеят най-много около денонощие.</li>
        {/* verified: migrations/0013_reviews.sql (cascade from user) */}
        <li>Отзивите — докато не ги изтриете или не изтриете профила; премахнат отзив се пази скрит, с причината, до изтриването на профила.</li>
        {/* verified: https://developers.cloudflare.com/d1/reference/time-travel/ (7 days on Workers Free); the account is on Workers Free (dashboard, confirmed by the maintainer 28 Sep) */}
        <li>Изтритите данни може да останат в резервните копия на базата данни в Cloudflare до 7 дни.</li>
        {/* verified: https://resend.com/security/gdpr (message content and logs retained 30 days while the account is active). Qualifies „изтрива всичко“ above, as the backups line does. Approved by the maintainer, 30 Sep, #65. */}
        <li>Копията на писмата, които сайтът ви е изпратил, остават при Resend до 30 дни, включително след изтриване на профила.</li>
      </ul>

      <h2>Какво можете да поискате</h2>
      <ul>
        {/* verified: src/app/routes/Account.tsx (self-service deletion) */}
        <li>
          <strong>Изтриване.</strong> Можете сами да изтриете профила си от страницата <Link to="/account">Профил</Link>.
        </li>
        <li>
          <strong>Копие и поправка.</strong> Пишете ни и ще ви изпратим копие на данните, които пазим за вас, или ще поправим неверни.
        </li>
        {/* GDPR Art. 20: applies because account data rests on the contract (see „На какво основание“). What a copy contains, compiled by hand on request: the address, the nickname if there is one, and their timestamps (migrations/0002_better_auth.sql, 0003), the reader's reviews, including any removed one with its reason (0013), every shelf entry with its book, status, owned, rating, note and date (0011), and the quiz result with its answers, two series and date (0012). Nothing else is keyed to the account. */}
        <li>
          <strong>Преносимост.</strong> Данните на профила ви можете да получите и в машинночетим вид (JSON), за да ги пренесете другаде.
        </li>
      </ul>
      <p>
        За всяко от тях пишете на <a href="mailto:contact@chetiporeda.com">contact@chetiporeda.com</a>. Имате право и на жалба до
        Комисията за защита на личните данни.
      </p>

      <h2>Право на възражение</h2>
      <p>
        {/* GDPR Art. 21: stated explicitly and apart from the other rights, because attempt counting, bot protection, analytics and the contact form rest on legitimate interests (see „На какво основание“) */}
        Когато обработваме данни на основание законен интерес — броенето на опити, защитата срещу ботове, преброяването на посещенията
        и формата за контакт, — имате право по всяко време да възразите срещу тази обработка по причини, свързани с вашето положение.
        Пишете на <a href="mailto:contact@chetiporeda.com">contact@chetiporeda.com</a>. Ще спрем, освен ако нямаме убедителни законни
        основания да продължим, които надделяват над интересите ви — например да спрем злоупотреба със сайта.
      </p>

      <h2>Деца</h2>
      <p>
        {/* verified: .claude/skills/legal-pages/SKILL.md (digital-consent age 14 in Bulgaria; no age check is built); the account is the only thing that collects personal data (see above) */}
        Профили не се предлагат на деца под 14 години без съгласието на родител. Ако разберем, че профил принадлежи на дете под 14
        години, ще го изтрием.
      </p>

      <h2>Промени</h2>
      <p>Ако нещо тук се промени, датата горе се сменя сама с промяната.</p>
    </>
  )
}

export function PrivacyEn() {
  return (
    <>
      <h2>Who is responsible</h2>
      <p>
        The site is run by one person, not a company: Petar Krastanov, who is responsible for your data. For anything about it, write to{' '}
        <a href="mailto:contact@chetiporeda.com">contact@chetiporeda.com</a> or use the <Link to="/contact">contact form</Link>.
      </p>

      <h2>What is collected, and why</h2>
      <h3>If you make an account</h3>
      <ul>
        <li>Your email address, to sign you in and to send the confirmation and new-password emails.</li>
        <li>
          A nickname, if you choose one: optional, and you can change or remove it from your account. Only you see it, unless you write a
          review: then it appears beside it. It is kept until you change it or delete your account.
        </li>
        <li>Your password, of which only a hash (scrypt) is kept, never the password itself.</li>
        <li>While you are signed in: a session record with a secret key and an expiry. The IP address and browser you signed in from are not kept in it.</li>
        <li>The confirmation link lasts 24 hours and the new-password link one hour. Each works once.</li>
      </ul>
      <h3>What you mark in your account</h3>
      <ul>
        <li>
          The books you have marked: whether you want to read it, are reading it or have read it, whether you own it, your rating from 1 to
          5, and your note. Your progress through the series is worked out from these and is not stored separately.
        </li>
        <li>Your latest quiz result: your answers, the two series it showed, and when.</li>
      </ul>
      <p>
        Only you can see them. There is no public profile and nobody else can read them. They are kept until you change them or delete your
        account.{' '}
        {/* verified: a translation of the Bulgarian sentence above */}
        If you publish a note as a review, its text becomes public, as a new review or added to the end of the review you already have of
        the book, and the note is cleared.
      </p>

      <h3>If you write a review</h3>
      <ul>
        <li>
          A review is public: anyone can see it on the book's page, with your nickname beside it, or as “A reader” if you have none. Being
          public, it can be found by search engines too. If you change or remove your nickname, all your reviews show the new one, or “A
          reader”, at once; search engines may keep the old one until they next visit the page. We keep its text, whether it contains
          spoilers, and when it was written and changed. Until it is removed, you can delete it at any time, and edit it unless your
          review writing is suspended (see below).
        </li>
        <li>
          If the person running the site removes a review, it is no longer shown, and the reason is emailed to you. The removed review and
          the reason are kept hidden until you delete your account. You can write a new review of the same book.
        </li>
        <li>
          Every removal counts. After the second, you cannot write or edit reviews for 30 days; after the third, you no longer can. Your
          account, the books you marked, your ratings, notes and quiz result all stay, and you can delete your reviews that have not been removed at any time. A removal is recorded. That record is cleared when the account is deleted.
          If you think a mistake has been made, write to us.
        </li>
        <li>
          Anyone can report a review, with or without an account. With the report we keep only the reason, if one is given, and nothing
          about who sent it. The report is emailed to the person running the site.
        </li>
      </ul>

      <h3>If you write to us</h3>
      <p>
        The contact form asks only for a message. A name and an email address are optional; the address is only so we can reply. The
        message is sent by email and is not stored in the site's database.
      </p>

      <h3>What arrives on its own</h3>
      <ul>
        <li>
          The site runs on Cloudflare. As the site's host, Cloudflare processes every request (it sees your IP address, your browser and
          the page you asked for) and keeps its own logs of them under{' '}
          <a href="https://www.cloudflare.com/privacypolicy/" rel="noopener">its own privacy policy</a>.
        </li>
        <li>
          Visits are counted with Cloudflare Web Analytics. Cloudflare adds the script, which loads from static.cloudflareinsights.com,
          and the report of the visit goes to the site itself. It sets no cookie and stores nothing in your browser.
        </li>
        <li>
          To stop abuse, the site counts attempts to register, sign in, request a new password, send a message and report a review: by IP
          address, and, for attempts against a particular email address, as a keyed hash rather than the address. Reviews written are
          counted per account.
        </li>
        <li>The site keeps technical error logs in Cloudflare. Email addresses in them are masked to the first letter and the domain.</li>
      </ul>

      <h2>On what basis</h2>
      <ul>
        <li>Account data: because it is needed to give you the account you asked for (performance of a contract).</li>
        <li>Counting attempts, bot protection, counting visits and the contact form: on legitimate interests, to protect the site from abuse, to know how many people read it, and to be able to answer when you write.</li>
      </ul>

      <h2>What is not collected</h2>
      <p>No advertising, no tracking across sites, no profiling, no sale of data. No decision about you is made automatically.</p>

      <h2>Cookies and browser storage</h2>
      <ul>
        <li>
          One cookie, and only if you sign in: the session cookie. Scripts cannot read it, and it expires 30 days after you sign in.
          Signing out ends it at once.
        </li>
        <li>Once you sign in, your session lasts at most 30 days. After that you have to sign in again, even if you have used the site in the meantime.</li>
        <li>If you change the language, your choice is kept in your browser (localStorage, key chetiporeda.locale). Nothing else is kept there.</li>
      </ul>

      <h2>Who else sees the data</h2>
      <ul>
        <li>
          <strong>Cloudflare</strong>: hosting, the network, the database and visit analytics. It processes the data under its{' '}
          <a href="https://www.cloudflare.com/cloudflare-customer-dpa/" rel="noopener">Data Processing Addendum</a>, which applies the
          EU Standard Contractual Clauses to transfers outside the EEA. Its{' '}
          <a href="https://www.cloudflare.com/gdpr/subprocessors/" rel="noopener">sub-processors</a> are in its own list.
        </li>
        <li>
          <strong>Cloudflare Turnstile</strong>: the bot check on registration, new password, the contact form and reporting a review. It processes your IP
          address, TLS fingerprint and browser, and the site's key with its address. When blocking bots, Cloudflare processes these on the
          site's behalf; when using them to improve Turnstile itself, it does so as a controller in its own right, on its legitimate
          interests (<a href="https://www.cloudflare.com/turnstile-privacy-policy/" rel="noopener">Turnstile's policy</a>).
        </li>
        <li>
          <strong>Resend</strong> sends the site's email. It receives the address we write to and the whole message, including any link in
          it. For the contact form it receives your message, and your name and address if you gave them. The mail is sent from Ireland,
          but Resend keeps a copy of it, and its records of sending it, in the United States: for 30 days, according to Resend. The
          transfer is under the EU Standard Contractual Clauses in its{' '}
          <a href="https://resend.com/legal/dpa" rel="noopener">Data Processing Addendum</a>. Its{' '}
          <a href="https://resend.com/legal/subprocessors" rel="noopener">sub-processors</a> are in its own list.
        </li>
        <li>
          <strong>Google</strong>: contact-form messages and mail to the site's addresses are forwarded to the site owner's mailbox,
          which is at Google.
        </li>
        <li>
          <strong>The bookshops the links lead to</strong>, only if you follow a link to one. Your browser then tells that shop you came
          from chetiporeda.com, but not from which page.
        </li>
      </ul>

      <h2>Where the data is</h2>
      <p>
        The database is at Cloudflare, under its EU jurisdiction: it is stored and runs in the European Union and is not copied outside it.
        The pages that read it are served from the Cloudflare data centre nearest to you, which may be outside the EU.
      </p>

      <h2>How long it is kept</h2>
      <ul>
        <li>
          Account data, with the books you have marked and your quiz result: until you delete the account. Deletion is immediate and
          removes everything tied to the account. At the moment of deletion the site sends one last email to the account's address,
          saying the account has been deleted.
        </li>
        <li>An unconfirmed account is deleted after 7 days.</li>
        <li>Confirmation and new-password links, and per-address attempt counters, are deleted every night once they expire.</li>
        <li>Per-IP attempt counters are deleted by the nightly clean-up once they are more than an hour old, so each lives for about a day at most.</li>
        <li>Reviews: until you delete them or your account; a removed review is kept hidden, with its reason, until the account is deleted.</li>
        <li>Deleted data may remain in Cloudflare's database backups for up to 7 days.</li>
        <li>Copies of the emails the site has sent you stay with Resend for up to 30 days, even after you delete your account.</li>
      </ul>

      <h2>What you can ask for</h2>
      <ul>
        <li>
          <strong>Deletion.</strong> You can delete your account yourself from the <Link to="/account">account page</Link>.
        </li>
        <li>
          <strong>A copy, and correction.</strong> Write to us and we will send you a copy of the data we hold about you, or correct what is
          wrong.
        </li>
        <li>
          <strong>Portability.</strong> You can also have your account data in a machine-readable form (JSON), to take it elsewhere.
        </li>
      </ul>
      <p>
        For any of these, write to <a href="mailto:contact@chetiporeda.com">contact@chetiporeda.com</a>. You also have the right to
        complain to the Bulgarian Commission for Personal Data Protection.
      </p>

      <h2>The right to object</h2>
      <p>
        Where we process data on legitimate interests (counting attempts, bot protection, counting visits and the contact form), you have
        the right to object to that processing at any time, on grounds relating to your particular situation. Write to{' '}
        <a href="mailto:contact@chetiporeda.com">contact@chetiporeda.com</a>. We will stop unless we have compelling legitimate grounds that
        override your interests, such as stopping abuse of the site.
      </p>

      <h2>Children</h2>
      <p>
        Accounts are not offered to children under the age of 14 without a parent's consent. If we learn that an account belongs to a
        child under the age of 14, it will be deleted.
      </p>

      <h2>Changes</h2>
      <p>If anything here changes, the date above changes with it, by itself.</p>
    </>
  )
}
